import React, { forwardRef } from 'react'
import NumericFormat, { INumericFormatProps } from 'transfix-ui/components/NumericFormat'
import { createStyles, makeStyles } from 'transfix-ui/core'
import { brandColors, TBrandColor } from 'transfix-ui/theme/colors'

interface IUseCurrencyInputTransparentStylesProps {
  invalid: boolean | undefined
  validationMessageColor: string
}

const useCurrencyInputTransparentStyles = makeStyles(() =>
  createStyles<string, IUseCurrencyInputTransparentStylesProps>({
    input: {
      '.currency-input-transparent &': {
        // This is a work around to override font-weight for the input component.
        // You can't add !importantfor fontWeight in a makeStyles
        fontWeight: 700,
      },
      // overrides default `input` component styles from 'scss/5_components/_text-input.scss'
      border: 'none !important',
      textAlign: 'center',
      fontSize: '34px !important',
    },
    validationMessage: {
      fontSize: '14px',
      textAlign: 'center',
      color: ({ invalid, validationMessageColor }) =>
        invalid ? brandColors.error : validationMessageColor,
    },
  })
)

type TPickedProp = 'id' | 'placeholder' | 'value'

export interface ICurrencyInputTransparentProps extends Pick<INumericFormatProps, TPickedProp> {
  validationMessage?: string
  validationMessageColor?: TBrandColor
  dataTest?: string
  ariaLabel?: string
  invalid?: boolean
  autoFocus?: boolean
  onChange: (e: { value: string; id: string }) => void
}

export const CurrencyInputTransparent = forwardRef<
  HTMLInputElement,
  ICurrencyInputTransparentProps
>(
  (
    {
      ariaLabel,
      autoFocus,
      dataTest = 'currency-input-transparent',
      id = '',
      invalid,
      onChange,
      placeholder = '$0',
      validationMessage,
      validationMessageColor: validationColorKey = 'coolGray0',
      value = '',
    },
    ref
  ) => {
    const validationMessageColor = brandColors[validationColorKey] // hex value
    const onChangeHandler = ({ value: eventValue }: { value: string; formattedValue: string }) => {
      onChange({ value: eventValue, id })
    }

    const classes = useCurrencyInputTransparentStyles({ invalid, validationMessageColor })

    return (
      <div className='currency-input-transparent'>
        <NumericFormat
          getInputRef={ref}
          className={classes.input}
          type='text'
          prefix='$'
          autoFocus={autoFocus}
          displayType='input'
          value={value}
          id={id}
          onValueChange={onChangeHandler}
          placeholder={placeholder}
          thousandSeparator
          valueIsNumericString
          data-test={dataTest}
          aria-label={ariaLabel}
        />
        {validationMessage && <div className={classes.validationMessage}>{validationMessage}</div>}
      </div>
    )
  }
)

export default CurrencyInputTransparent
